// src/warehouse/index.js — екран „Склад" (ПРОМТ-8): наличности, картон на
// артикул, оборотна ведомост с Excel, инвентаризация и ръчно движение.
//
// Бекендът (routes/stock.js, services/stock.js) пази правилата: регистърът е
// само за добавяне, себестойността се смята на сървъра, заключеният период се
// проверява там. Тук е само показването. Екранът се вижда само при фирма с
// warehouse_mode „trade" или „materials" (viewAllowed в shell.js).
// ПРОМТ-13: при „materials" (ресторант) има още два раздела — „Продажби от
// POS" (pos.js) и „Рецептури" (recipes.js).
// Видът на списъците е като в „Доставки" (Макс, 29.10.2026: „всички менюта,
// които са тип таблици, да изглеждат като Доставки") — общите парчета от
// src/shared/register.js: бутоните на реда на заглавието (основният последен),
// всеки филтър с надпис отгоре, броячът над таблицата, по-ниските редове.

import { el, clear, $, field, input, row } from '../shared/dom.js';
import { dateInput, isoToBg, localTodayIso } from '../shared/dates.js';
import { toast } from '../shared/toast.js';
import { api } from '../shared/api.js';
import { loadError, searchInput, emptyState, tabBar } from '../shared/ui.js';
import { openModal, closeModal, confirmDialog } from '../shared/modal.js';
import { xlsxDownload } from '../shared/xlsx.js';
import { state, cq, activeCompanyObj } from '../shared/state.js';
import { amount, quantity, plural } from '../shared/format.js';
import { posTab, resetPos } from './pos.js';
import { recipesTab, openDish } from './recipes.js';
import { importDialog } from '../invoices/articles.js';
import { kontoExportDialog, monthSelect, renderExportRuns, newElementsBox } from '../shared/kontoExport.js';
import { combobox } from '../shared/combobox.js';
import { packCheckDialog } from '../shared/remeasure.js';
import { regHead, regFilters, regField, regTools, regCount, REG_TABLE } from '../shared/register.js';

let _tab = 'balances';
let _q = '';
let _onlyNeg = false;
let _onlyLow = false;
let _card = { article_id: '', from: '', to: '' };
let _turn = { from: '', to: '' };
// Дошли сме в „Рецептури" от отворен месец в „Продажби от POS" (ПРОМТ-13б):
// рецептурите показват „← Към продажбите от POS", а месецът остава отворен.
let _fromPos = false;

const TABS = [
  ['balances', 'Наличности'],
  ['card', 'Картон'],
  ['turnover', 'Оборотна ведомост'],
  ['counts', 'Инвентаризация'],
  ['manual', 'Ръчно движение'],
];
// Само за ресторант: търговецът няма ястия и касов софтуер без склад, а и
// сървърът отказва тези адреси за фирма, която не е „materials".
const RESTAURANT_TABS = [
  ['pos', 'Продажби от POS'],
  ['recipes', 'Рецептури'],
];
const isRestaurant = () => ((activeCompanyObj() || {}).warehouse_mode === 'materials');
// „Експорти" (ПРОМТ-11): изписванията за Конто и всички направени файлове —
// работа на кантората, клиентът не внася нищо в Конто.
const isStaffUser = () => { const u = state().user; return !!u && u.role !== 'client'; };
const tabsNow = () => (isRestaurant() ? TABS.concat(RESTAURANT_TABS) : TABS).concat(isStaffUser() ? [['exports', 'Експорти за Конто']] : []);
const MANUAL = [
  ['opening', 'Начално салдо'],
  ['receipt', 'Приход (ръчен)'],
  ['issue', 'Разход (ръчен)'],
  ['adjustment_in', 'Корекция + (излишък)'],
  ['adjustment_out', 'Корекция − (липса, брак)'],
];

const reload = () => view($('#page'));
const isAdmin = () => !!(state().user && state().user.role === 'admin');
// Сумите и количествата са със запетая — през общите функции, с които се
// показват и сумите във фактурите (src/shared/format.js).
const f2 = amount;
const fq = quantity;
const monthStart = () => localTodayIso().slice(0, 8) + '01';
const withQ = (params) => cq() + Object.keys(params).filter((k) => params[k] !== '' && params[k] != null).map((k) => '&' + k + '=' + encodeURIComponent(params[k])).join('');
const isoOf = (inp) => inp.dataset.iso || '';

// Отваря склада на „Рецептури" за едно ястие (връзката от „Артикули").
export function openRecipes(articleId) {
  _tab = 'recipes';
  openDish(articleId);
  state().view = 'warehouse';
  window.CRM.renderShell();
}

// Контекстният „? Помощ" (shell.js) пита екрана за котвата: на POS и
// рецептурите помощта е в отделен раздел.
export function helpAnchor() { return (_tab === 'pos' || _tab === 'recipes') && isRestaurant() ? 'h-pos' : 'h-sklad'; }

export async function view(page) {
  clear(page);
  if (!state().activeCompany) return window.CRM.needsCompany(page);
  const tabs = tabsNow();
  // Смяна на фирмата към търговска, докато е отворен ресторантски раздел.
  if (!tabs.some(([id]) => id === _tab)) _tab = 'balances';
  // Натиснат раздел = неговото начало (списъкът), не последно отвореното —
  // затова и повторното натискане на избрания раздел презарежда.
  page.appendChild(tabBar(tabs.map(([id, label]) => ({ id, label })), _tab,
    (id) => { _tab = id; _fromPos = false; if (id === 'pos') resetPos(); if (id === 'recipes') openDish(null); reload(); },
    'Раздели на склада'));
  page.appendChild(lockBar());
  const area = el('div');
  page.appendChild(area);
  const ctx = {
    reload,
    goTab: (id) => { _tab = id; if (id === 'recipes') openDish(null); reload(); },
    // От реда „Ястия без рецептура" в POS: право на картата на ястието (или
    // на списъка, за вноса от Excel), с път обратно към същия месец.
    openRecipe: (articleId) => { _tab = 'recipes'; _fromPos = true; openDish(articleId); reload(); },
    backToPos: _fromPos ? () => { _fromPos = false; _tab = 'pos'; reload(); } : null,
  };
  if (_tab === 'pos') return posTab(area, ctx);
  if (_tab === 'recipes') return recipesTab(area, ctx);
  if (_tab === 'card') return cardTab(area);
  if (_tab === 'turnover') return turnoverTab(area);
  if (_tab === 'counts') return countsTab(area);
  if (_tab === 'manual') return manualTab(area);
  if (_tab === 'exports') return exportsTab(area);
  return balancesTab(area);
}

// Заключеният от експорта период — видим за всички; отключва само админ.
function lockBar() {
  const c = activeCompanyObj() || {};
  if (!c.stock_lock_date) return el('div');
  return el('div', { class: 'sev-warn', role: 'status', style: 'display:flex;gap:10px;align-items:center;flex-wrap:wrap' },
    el('span', { text: 'Складът е заключен до ' + isoToBg(c.stock_lock_date) + ' (изнесен към Конто). Движения с по-ранна дата не се приемат.' }),
    isAdmin() ? el('button', { class: 'btn sm ghost', type: 'button', text: 'Отключи период', onClick: () => unlockDialog(c) }) : null);
}

function unlockDialog(c) {
  const d = dateInput('');
  const reason = el('textarea', { rows: '3', maxlength: '500' });
  const body = el('div', {},
    el('p', { text: 'Отключването мести датата назад. Празно поле = отключване изцяло. Причината влиза в историята на фирмата.' }),
    field('Заключено до (нова дата)', d, { size: 's' }),
    field('Причина', reason));
  const ok = el('button', { class: 'btn', type: 'button', text: 'Отключи', onClick: async () => {
    if (String(d.value || '').trim() && !d.dataset.iso) { toast('Датата не е разпозната — въведете я като дд.мм.гггг.', true); return; }
    if (!reason.value.trim()) { toast('Въведете причина.', true); reason.focus(); return; }
    try {
      const r = await api('/stock/unlock', { method: 'POST', body: JSON.stringify({ company_id: c.id, date: d.dataset.iso || '', reason: reason.value.trim() }) });
      c.stock_lock_date = r.stock_lock_date;
      closeModal();
      toast(r.stock_lock_date ? 'Складът е заключен до ' + isoToBg(r.stock_lock_date) + '.' : 'Складът е отключен изцяло.');
      reload();
    } catch (e) { toast(e.message, true); }
  } });
  openModal({ title: 'Отключване на склада', body, foot: el('div', {}, el('button', { class: 'btn ghost', type: 'button', text: 'Отказ', onClick: closeModal }), ok) });
}

// ---- Експорти за Конто (ПРОМТ-11) ----

function exportsTab(area) {
  const c = activeCompanyObj() || {};
  const panel = el('div', { class: 'panel' });
  const list = el('div');
  const cogsOff = !cogsEnabled(c);
  panel.appendChild(regHead('Експорти за Конто', [],
    cogsOff ? null : el('button', { class: 'btn sm', type: 'button', text: 'Изписвания за Конто', onClick: () => cogsDialog(() => { renderExportRuns(list); }) })));
  panel.appendChild(el('p', { class: 'muted', text: cogsOff
    ? 'Файлът с изписванията е изключен за тази фирма. Тук са всички файлове за Конто — „Свали същия файл" дава точно изнесения.'
    : 'Изписванията (себестойността на продаденото) се изнасят веднъж месечно. Тук са и всички файлове за Конто — „Свали същия файл" дава точно изнесения.' }));
  panel.appendChild(list);
  area.appendChild(panel);
  renderExportRuns(list);
}

// Изнася ли се себестойността — като kontoCogs.enabledOf на сървъра:
// изборът в „Настройки за Конто" → „Изписване", иначе старото поле на фирмата.
function cogsEnabled(c) {
  const e = c && c.konto && c.konto.cogs ? c.konto.cogs.export : '';
  if (e === 'no') return false;
  if (e === 'yes') return true;
  return !(c && c.konto_export_cogs === false);
}

// ПРОМТ-33: как ще се осчетоводи — сметките с партидите по нивата на
// фирмата в Конто ([код на артикула][код на мярката]) и новите елементи,
// които Конто ще създаде при вноса.
const lotText = (lot) => (lot && lot.length ? ' [' + lot.join('][') + ']' : '');
function cogsPreview(r) {
  const wrap = el('div', {});
  const items = Array.isArray(r.items) ? r.items : [];
  if (items.length) {
    const det = el('details', { style: 'margin-top:8px' }, el('summary', { text: 'Как ще се осчетоводи (' + plural(items.length, 'статия', 'статии') + ')' }));
    const ul = el('ul', { class: 'records-preview', style: 'margin:4px 0 0;padding-left:20px' });
    items.slice(0, 30).forEach((it) => ul.appendChild(el('li', {},
      el('div', { style: 'font-weight:600', text: it.label + ' · ' + it.name + ' · ' + quantity(it.qty) + ' ' + (it.measure || '') }),
      el('div', { class: 'muted', text: 'Дт ' + it.dt + lotText(it.dt_lot) + ' / Кт ' + it.kt + lotText(it.kt_lot) + ' · ' + amount(it.value) + ' €' }))));
    det.appendChild(ul);
    if (items.length > 30) det.appendChild(el('div', { class: 'muted', text: '…и още ' + (items.length - 30) }));
    wrap.appendChild(det);
  }
  const ne = newElementsBox(r);
  if (ne) wrap.appendChild(ne);
  return wrap.childNodes.length ? wrap : null;
}

function cogsDialog(done) {
  const month = monthSelect('Месец');
  return kontoExportDialog({
    kind: 'cogs', title: 'Изписвания за Конто', url: '/stock/konto-export',
    intro: 'Себестойността на продаденото за месеца — готови статии Дт/Кт, Конто нищо не преизчислява.',
    controls: [field('Месец', month, { size: 'm' })],
    payload: () => ({ period: month.value }),
    headline: (r) => plural(r.count, 'статия', 'статии') + ' · ' + amount(r.total) + ' €',
    sub: (r) => (r.already_locked ? 'Складът вече е заключен до ' + isoToBg(r.lock_to) + '.' : 'След свалянето складът се заключва до ' + isoToBg(r.lock_to) + ' — движения с по-ранна дата не се приемат.'),
    extra: (r) => cogsPreview(r),
    onDone: (r) => {
      const co = activeCompanyObj();
      if (co && r.stock_lock_date) co.stock_lock_date = r.stock_lock_date;
      if (done) done();
      reload();
      // Нови артикули в Конто — човекът трябва да знае какво да отговори на въпроса.
      const ne = newElementsBox(r);
      if (ne) openModal({ title: 'Файлът за Конто е готов', body: el('div', {}, ne), foot: el('div', {}, el('button', { class: 'btn', type: 'button', text: 'Затвори', onClick: closeModal })) });
    },
  });
}

// ---- Наличности ----

async function balancesTab(area) {
  let rows;
  try { rows = await api('/stock/balances' + cq()); }
  catch (e) { area.appendChild(loadError(() => { clear(area); balancesTab(area); }, e.message)); return; }
  if (!Array.isArray(rows)) rows = [];
  const panel = el('div', { class: 'panel' });
  // Ресторант (Макс, 27.10.2026): материалите в бройки → в кг/л, за да
  // пише рецептата грамове (src/shared/remeasure.js). Бутонът е на реда на
  // заглавието, а търсачката слезе във филтрите — като в „Доставки".
  panel.appendChild(regHead('Наличности',
    [isAdmin() && isRestaurant() ? el('button', { class: 'btn sm ghost', type: 'button', text: 'Материали в бройки', onClick: () => packCheckDialog(reload) }) : null]));
  // Нова фирма (ПРОМТ-16): нито една наличност — вместо празна таблица пътят
  // към началото е един бутон: таблицата с артикулите и наличностите.
  if (!rows.length || rows.every((r) => !Number(r.qty) && !Number(r.value))) {
    panel.appendChild(emptyState('▥', 'Още няма наличности. Попълнете таблицата и я качете.', 'Внос от Excel', () => importDialog()));
    area.appendChild(panel);
    return;
  }
  const negCount = rows.filter((r) => r.negative).length;
  const lowCount = rows.filter((r) => r.below_min).length;
  const chk = (on, text, set) => el('label', { style: 'display:inline-flex;gap:6px;align-items:center;cursor:pointer' },
    el('input', { type: 'checkbox', checked: on, onChange: (e) => { set(!!e.target.checked); draw(); } }), text);
  // Отметките са бързият избор под филтрите (regTools), както бутоните в
  // „Доставки"; броячът „N от M" е над таблицата.
  panel.appendChild(regFilters(
    regField('Код или име', searchInput({ placeholder: 'Код или име…', value: _q, ariaLabel: 'Търсене в наличностите', onSearch: (q) => { _q = q; draw(); } }), 'l'),
    regTools(
      chk(_onlyNeg, 'Отрицателни' + (negCount ? ' (' + negCount + ')' : ''), (v) => { _onlyNeg = v; }),
      chk(_onlyLow, 'Под минимума' + (lowCount ? ' (' + lowCount + ')' : ''), (v) => { _onlyLow = v; }))));
  const count = regCount();
  panel.appendChild(count);
  const tb = el('tbody');
  const total = el('td', { class: 'num', style: 'font-weight:700' });
  const note = el('div', { role: 'status', style: 'padding:10px 4px;color:var(--muted)' });
  function draw() {
    clear(tb);
    const list = rows.filter((r) => (!_onlyNeg || r.negative) && (!_onlyLow || r.below_min)
      && (!_q || [r.code, r.name].some((v) => String(v || '').toLowerCase().includes(_q))));
    let sum = 0;
    for (const r of list) {
      sum += Number(r.value) || 0;
      const flag = r.negative ? el('span', { class: 'tag red', text: 'отрицателна' }) : (r.below_min ? el('span', { class: 'tag amber', text: 'под минимума (' + fq(r.min_qty) + ')' }) : null);
      const tr = el('tr', { class: r.negative ? 'stock-neg' : (r.below_min ? 'stock-low' : '') },
        el('td', { style: 'font-variant-numeric:tabular-nums' }, r.code),
        el('td', { style: 'font-weight:600' }, r.name, flag ? ' ' : '', flag),
        el('td', {}, r.measure),
        el('td', { class: 'num' }, fq(r.qty)),
        el('td', { class: 'num' }, f2(r.avg)),
        el('td', { class: 'num' }, f2(r.value)),
        el('td', {}, el('button', { class: 'btn sm ghost', type: 'button', text: 'Картон', 'aria-label': 'Картон на ' + r.name,
          onClick: () => { _card = { article_id: r.article_id, from: _card.from, to: _card.to }; _tab = 'card'; reload(); } })));
      tb.appendChild(tr);
    }
    total.textContent = f2(sum);
    count.set(list.length, rows.length, _q);
    note.textContent = list.length ? '' : 'Нищо не отговаря на филтъра.';
    note.style.display = list.length ? 'none' : '';
  }
  panel.appendChild(el('table', { class: REG_TABLE },
    el('thead', {}, el('tr', {}, el('th', { text: 'Код' }), el('th', { text: 'Артикул' }), el('th', { text: 'Мярка' }),
      el('th', { class: 'num', text: 'Количество' }), el('th', { class: 'num', text: 'Средна цена' }), el('th', { class: 'num', text: 'Стойност' }), el('th', { text: '' }))),
    tb,
    el('tfoot', {}, el('tr', {}, el('td', { colspan: '5', text: 'Общо стойност (показаните)' }), total, el('td', {})))));
  panel.appendChild(note);
  draw();
  area.appendChild(panel);
}

// ---- Картон ----

// Търсачка за артикула (Макс, 21.10.2026: „като при артикулите") вместо
// падащ списък: пише се част от името или кода. sel.value дава избрания
// артикул (или '') — както досега дава падащият списък, затова картонът и
// ръчното движение не се пренаписват; onPick(row) — при избор.
async function articleOptions(current, onPick) {
  let rows = [];
  try { rows = await api('/stock/balances' + cq()); } catch (e) { rows = []; }
  rows = rows || [];
  const sel = combobox({
    items: rows, idOf: (r) => r.article_id,
    label: (r) => (r.code ? r.code + ' ' : '') + r.name + ' (' + r.measure + ')',
    sub: (r) => 'налични: ' + fq(r.qty) + ' ' + (r.measure || ''),
    search: (r) => (r.code || '') + ' ' + (r.name || ''),
    value: current || null, placeholder: 'Пишете име или код на артикула…',
    emptyText: 'Няма такъв артикул на склад.', autoPickSingle: true,
    onPick: (r) => { if (onPick) onPick(r); },
  });
  Object.defineProperty(sel, 'value', { get: () => { const v = sel.getValue(); return v == null ? '' : String(v); } });
  return { sel, rows };
}

async function cardTab(area) {
  const panel = el('div', { class: 'panel' }, regHead('Картон на артикул', []));
  area.appendChild(panel);
  const { sel } = await articleOptions(_card.article_id);
  const from = dateInput(_card.from || '');
  const to = dateInput(_card.to || '');
  const out = el('div');
  const show = el('button', { class: 'btn sm', type: 'button', text: 'Покажи', onClick: () => {
    _card = { article_id: sel.value, from: isoOf(from), to: isoOf(to) };
    load();
  } });
  // „Покажи" е в края на лентата с филтрите, долу до полетата.
  panel.appendChild(regFilters(regField('Артикул', sel, 'l'), regField('От', from, 's'), regField('До', to, 's'), show));
  panel.appendChild(out);
  async function load() {
    clear(out);
    if (!_card.article_id) { out.appendChild(el('p', { class: 'cal-hint', text: 'Изберете артикул и период.' })); return; }
    let data;
    try { data = await api('/stock/card' + withQ({ article_id: _card.article_id, from: _card.from, to: _card.to })); }
    catch (e) { out.appendChild(loadError(load, e.message)); return; }
    const tb = el('tbody');
    tb.appendChild(el('tr', { class: 'row-inactive' }, el('td', { colspan: '6', text: 'Начално салдо' }),
      el('td', { class: 'num', text: fq(data.opening.qty) }), el('td', { class: 'num', text: f2(data.opening.value) }), el('td', {})));
    for (const m of data.rows) {
      const inbound = m.direction > 0;
      const isPos = m.source_type === 'pos_sale';
      const docBtn = isPos && m.doc_id != null
        ? el('button', { class: 'btn sm ghost', type: 'button', text: 'Разписка', 'aria-label': 'Разходна разписка за POS ' + (m.doc_number || ''),
          onClick: () => window.open('/api/pos-sales/' + m.doc_id + '/pdf', '_blank') })
        : m.doc_id != null && m.source_type === 'invoice' && m.doc_number
        ? el('button', { class: 'btn sm ghost', type: 'button', text: 'Разписка', 'aria-label': 'Складова разписка към ' + m.doc_label + ' ' + m.doc_number,
          onClick: () => window.open('/api/stock/docs/invoice/' + m.doc_id + '/pdf', '_blank') })
        : el('button', { class: 'btn sm ghost', type: 'button', text: 'Разписка', 'aria-label': 'Складова разписка за движение ' + m.id,
          onClick: () => window.open('/api/stock/movements/' + m.id + '/pdf', '_blank') });
      tb.appendChild(el('tr', { class: m.type === 'reversal' ? 'row-inactive' : '' },
        el('td', {}, isoToBg(m.date)),
        el('td', {}, m.type_label, m.cost_pending ? el('span', { class: 'tag amber', style: 'margin-left:6px', text: 'чакаща цена' }) : null),
        // POS: „POS Септември 2026" (месецът не е номер) + връзка към оригинала.
        el('td', {}, isPos ? (m.doc_label || 'POS') + (m.doc_number ? ' ' + m.doc_number : '') : m.doc_label + (m.doc_number ? ' № ' + m.doc_number : ''),
          isPos && m.file_url ? el('a', { href: m.file_url, target: '_blank', rel: 'noopener', style: 'margin-left:6px', text: 'оригинал', 'aria-label': 'Оригиналът на POS таблицата за ' + (m.doc_number || '') }) : null,
          m.note ? el('div', { style: 'color:var(--muted);font-size:var(--fs-sm)', text: m.note }) : null),
        el('td', { class: 'num' }, inbound ? fq(m.qty) : ''),
        el('td', { class: 'num' }, inbound ? '' : fq(m.qty)),
        el('td', { class: 'num' }, m.cost_pending ? '—' : f2(m.unit_cost)),
        el('td', { class: 'num' }, fq(m.balance_qty)),
        el('td', { class: 'num' }, f2(m.balance_value)),
        el('td', {}, docBtn)));
    }
    out.appendChild(el('table', { class: REG_TABLE },
      el('thead', {}, el('tr', {}, el('th', { text: 'Дата' }), el('th', { text: 'Движение' }), el('th', { text: 'Документ' }),
        el('th', { class: 'num', text: 'Приход' }), el('th', { class: 'num', text: 'Разход' }), el('th', { class: 'num', text: 'Ед. цена' }),
        el('th', { class: 'num', text: 'Наличност' }), el('th', { class: 'num', text: 'Стойност' }), el('th', { text: '' }))),
      tb));
    out.appendChild(el('p', { class: 'cal-hint', text: 'Крайно салдо: ' + fq(data.closing.qty) + ' ' + data.article.measure + ' на стойност ' + f2(data.closing.value) + '. Сторната остават в картона — движение не се трие.' }));
  }
  load();
}

// ---- Оборотна ведомост ----

async function turnoverTab(area) {
  const panel = el('div', { class: 'panel' });
  area.appendChild(panel);
  const from = dateInput(_turn.from || monthStart());
  const to = dateInput(_turn.to || localTodayIso());
  const out = el('div');
  let last = null;
  const xlsBtn = el('button', { class: 'btn sm ghost', type: 'button', text: 'Excel', disabled: 'disabled', onClick: () => {
    if (!last) return;
    const data = last.rows.map((r) => ({
      'Код': r.code, 'Артикул': r.name, 'Мярка': r.measure,
      'Начално к-во': r.opening_qty, 'Начална стойност': r.opening_value,
      'Приход к-во': r.in_qty, 'Приход стойност': r.in_value,
      'Разход к-во': r.out_qty, 'Разход стойност': r.out_value,
      'Крайно к-во': r.closing_qty, 'Крайна стойност': r.closing_value,
    }));
    data.push({ 'Код': '', 'Артикул': 'ОБЩО', 'Мярка': '', 'Начално к-во': '', 'Начална стойност': last.totals.opening_value, 'Приход к-во': '', 'Приход стойност': last.totals.in_value, 'Разход к-во': '', 'Разход стойност': last.totals.out_value, 'Крайно к-во': '', 'Крайна стойност': last.totals.closing_value });
    xlsxDownload(data, 'Оборотна ведомост', 'oborotna-vedomost-' + last.from + '-' + last.to + '.xlsx');
  } });
  // ПРОМТ-33: по подразбиране без артикулите, при които всичко е нула (нито
  // наличност, нито движение) — отметката ги показва. Excel-ът следва нея.
  const allBox = el('input', { type: 'checkbox' });
  allBox.checked = !!_turn.all;
  allBox.addEventListener('change', () => { _turn = Object.assign({}, _turn, { all: allBox.checked }); if (_turn.from) load(); });
  const show = el('button', { class: 'btn sm', type: 'button', text: 'Покажи', onClick: () => { _turn = { from: isoOf(from), to: isoOf(to), all: allBox.checked }; load(); } });
  // Excel е на реда на заглавието (както експортите в „Доставки"), периодът и
  // „Покажи" — във филтрите, а отметката — бързият избор под тях.
  panel.appendChild(regHead('Складова оборотна ведомост', [xlsBtn]));
  panel.appendChild(regFilters(regField('От', from, 's'), regField('До', to, 's'), show,
    regTools(el('label', { class: 'choice' }, allBox, el('span', { text: 'Покажи и без движение' })))));
  panel.appendChild(out);
  async function load() {
    clear(out);
    const f = _turn.from || isoOf(from);
    const t = _turn.to || isoOf(to);
    try { last = await api('/stock/turnover' + withQ(Object.assign({ from: f, to: t }, _turn.all ? { all: '1' } : {}))); }
    catch (e) { last = null; xlsBtn.setAttribute('disabled', 'disabled'); out.appendChild(loadError(load, e.message)); return; }
    xlsBtn.removeAttribute('disabled');
    const hiddenNote = last.hidden && !_turn.all ? el('p', { class: 'muted', text: 'Без наличност и без движение (скрити): ' + plural(last.hidden, 'артикул', 'артикула') + '. „Покажи и без движение“ ги показва.' }) : null;
    if (!last.rows.length) { out.appendChild(el('p', { class: 'cal-hint', text: 'Няма движения до края на периода.' })); if (hiddenNote) out.appendChild(hiddenNote); return; }
    // „N от M": M брои и скритите без движение — да се вижда, че не са всички.
    const count = regCount();
    count.set(last.rows.length, last.rows.length + (_turn.all ? 0 : Number(last.hidden) || 0));
    out.appendChild(count);
    const tb = el('tbody');
    for (const r of last.rows) {
      tb.appendChild(el('tr', {}, el('td', {}, r.code), el('td', {}, r.name), el('td', {}, r.measure),
        el('td', { class: 'num' }, fq(r.opening_qty)), el('td', { class: 'num' }, f2(r.opening_value)),
        el('td', { class: 'num' }, fq(r.in_qty)), el('td', { class: 'num' }, f2(r.in_value)),
        el('td', { class: 'num' }, fq(r.out_qty)), el('td', { class: 'num' }, f2(r.out_value)),
        el('td', { class: 'num' }, fq(r.closing_qty)), el('td', { class: 'num' }, f2(r.closing_value))));
    }
    out.appendChild(el('table', { class: REG_TABLE },
      el('thead', {}, el('tr', {}, el('th', { text: 'Код' }), el('th', { text: 'Артикул' }), el('th', { text: 'Мярка' }),
        el('th', { class: 'num', text: 'Нач. к-во' }), el('th', { class: 'num', text: 'Нач. стойност' }),
        el('th', { class: 'num', text: 'Приход к-во' }), el('th', { class: 'num', text: 'Приход стойност' }),
        el('th', { class: 'num', text: 'Разход к-во' }), el('th', { class: 'num', text: 'Разход стойност' }),
        el('th', { class: 'num', text: 'Кр. к-во' }), el('th', { class: 'num', text: 'Кр. стойност' }))),
      tb,
      el('tfoot', {}, el('tr', { style: 'font-weight:700' }, el('td', { colspan: '3', text: 'Общо' }),
        el('td', {}), el('td', { class: 'num', text: f2(last.totals.opening_value) }),
        el('td', {}), el('td', { class: 'num', text: f2(last.totals.in_value) }),
        el('td', {}), el('td', { class: 'num', text: f2(last.totals.out_value) }),
        el('td', {}), el('td', { class: 'num', text: f2(last.totals.closing_value) })))));
    // Бележката за скритите е под таблицата — броячът горе вече казва „N от M".
    if (hiddenNote) out.appendChild(hiddenNote);
  }
  if (_turn.from) load();
}

// ---- Инвентаризация ----

async function countsTab(area) {
  let list;
  try { list = await api('/stock/counts' + cq()); }
  catch (e) { area.appendChild(loadError(() => { clear(area); countsTab(area); }, e.message)); return; }
  const panel = el('div', { class: 'panel' });
  panel.appendChild(regHead('Инвентаризация', [],
    el('button', { class: 'btn sm', type: 'button', text: '+ Нов лист', onClick: newCountDialog })));
  if (!list.length) {
    panel.appendChild(emptyState('☷', 'Още няма инвентаризации. Новият лист взема наличностите към избраната дата.'));
    area.appendChild(panel);
    return;
  }
  const count = regCount();
  count.set(list.length, list.length);
  panel.appendChild(count);
  const tb = el('tbody');
  for (const c of list) {
    tb.appendChild(el('tr', {},
      el('td', {}, c.number),
      el('td', {}, isoToBg(c.date)),
      el('td', {}, c.status === 'confirmed' ? el('span', { class: 'tag green', text: 'потвърдена' }) : el('span', { class: 'tag amber', text: 'чернова' })),
      el('td', { class: 'num' }, String(c.lines_count || 0)),
      el('td', {}, el('div', { class: 'row-actions' },
        el('button', { class: 'btn sm ghost', type: 'button', text: c.status === 'draft' ? 'Въведи преброено' : 'Преглед', 'aria-label': (c.status === 'draft' ? 'Въведи преброено, ' : 'Преглед, ') + c.number, onClick: () => countDialog(c.id) }),
        el('button', { class: 'btn sm ghost', type: 'button', text: 'Протокол PDF', 'aria-label': 'Протокол PDF, ' + c.number, onClick: () => window.open('/api/stock/counts/' + c.id + '/pdf', '_blank') })))));
  }
  panel.appendChild(el('table', { class: REG_TABLE },
    el('thead', {}, el('tr', {}, el('th', { text: 'Номер' }), el('th', { text: 'Към дата' }), el('th', { text: 'Статус' }), el('th', { class: 'num', text: 'Артикули' }), el('th', { text: '' }))),
    tb));
  area.appendChild(panel);
}

function newCountDialog() {
  const d = dateInput(localTodayIso());
  const ok = el('button', { class: 'btn', type: 'button', text: 'Създай лист', onClick: async () => {
    if (!d.dataset.iso) { toast('Изберете дата.', true); return; }
    try {
      const c = await api('/stock/counts', { method: 'POST', body: JSON.stringify({ company_id: state().activeCompany, date: d.dataset.iso }) });
      closeModal();
      toast('Създаден лист ' + c.number + '.');
      countDialog(c.id);
    } catch (e) { toast(e.message, true); }
  } });
  openModal({ title: 'Нов лист за инвентаризация', body: el('div', {}, el('p', { text: 'Системните количества се вземат към датата. При потвърждаване се преизчисляват наново — ако междувременно е имало движение.' }), field('Към дата', d, { size: 's' })),
    foot: el('div', {}, el('button', { class: 'btn ghost', type: 'button', text: 'Отказ', onClick: closeModal }), ok) });
}

async function countDialog(id) {
  let c;
  let arts = [];
  try {
    c = await api('/stock/counts/' + id);
    arts = await api('/stock/balances' + cq());
  } catch (e) { toast(e.message, true); return; }
  const info = {};
  (arts || []).forEach((a) => { info[a.article_id] = a; });
  const draft = c.status === 'draft';
  const inputs = {};
  const tb = el('tbody');
  (c.lines || []).forEach((l) => {
    const a = info[l.article_id] || { code: '', name: 'Артикул ' + l.article_id, measure: '' };
    const diffCell = el('td', { class: 'num' });
    const syncDiff = (v) => {
      const s = String(v == null ? '' : v).trim();
      if (s === '') { diffCell.textContent = '—'; return; }
      const d = Math.round((Number(s) - Number(l.system_qty || 0)) * 10000) / 10000;
      diffCell.textContent = (d > 0 ? '+' : '') + fq(d);
    };
    // Таблица с <input> в редовете, не редактируеми <td> (правило за
    // достъпност): всяко поле има име с артикула.
    const cell = draft
      ? (inputs[l.article_id] = input({ type: 'number', step: 'any', min: '0', value: l.counted_qty == null ? '' : String(l.counted_qty),
        'aria-label': 'Преброено, ' + a.name, style: 'width:110px', onInput: (e) => syncDiff(e.target.value) }))
      : (l.counted_qty == null ? '—' : fq(l.counted_qty));
    syncDiff(l.counted_qty);
    tb.appendChild(el('tr', {}, el('td', {}, a.code), el('td', {}, a.name), el('td', {}, a.measure),
      el('td', { class: 'num' }, fq(l.system_qty)), el('td', { class: 'num' }, cell), diffCell));
  });
  const collect = () => Object.keys(inputs).map((k) => ({ article_id: Number(k), counted_qty: inputs[k].value.trim() === '' ? null : Number(inputs[k].value) }));
  const save = async () => {
    const bad = collect().find((l) => l.counted_qty != null && !(l.counted_qty >= 0));
    if (bad) { toast('Преброеното трябва да е число ≥ 0.', true); return false; }
    try { await api('/stock/counts/' + c.id, { method: 'PUT', body: JSON.stringify({ lines: collect() }) }); return true; }
    catch (e) { toast(e.message, true); return false; }
  };
  const foot = el('div', {},
    el('button', { class: 'btn ghost', type: 'button', text: 'Протокол PDF', onClick: () => window.open('/api/stock/counts/' + c.id + '/pdf', '_blank') }),
    el('button', { class: 'btn ghost', type: 'button', text: 'Затвори', onClick: () => { closeModal(); reload(); } }));
  if (draft) {
    foot.appendChild(el('button', { class: 'btn ghost', type: 'button', text: 'Запази', onClick: async () => { if (await save()) toast('Преброеното е запазено.'); } }));
    foot.appendChild(el('button', { class: 'btn', type: 'button', text: 'Потвърди', onClick: async () => {
      if (!(await save())) return;
      const ok = await confirmDialog('Да потвърдя ли инвентаризацията? Разликите стават корекции в склада и листът се заключва. Непреброените редове не се пипат.', { okText: 'Потвърди' });
      if (!ok) return;
      try {
        await api('/stock/counts/' + c.id + '/confirm', { method: 'POST', body: JSON.stringify({}) });
        closeModal();
        toast('Инвентаризация ' + c.number + ' е потвърдена.');
        reload();
      } catch (e) { toast(e.message, true); }
    } }));
  }
  openModal({ title: 'Инвентаризация ' + c.number + ' към ' + isoToBg(c.date), wide: true,
    body: el('div', {},
      draft ? el('p', { class: 'cal-hint', text: 'Въведете преброеното количество в базовата мярка. Празно поле = непреброено (без корекция).' }) : null,
      el('table', { class: 'list' },
        el('thead', {}, el('tr', {}, el('th', { text: 'Код' }), el('th', { text: 'Артикул' }), el('th', { text: 'Мярка' }),
          el('th', { class: 'num', text: 'По книги' }), el('th', { class: 'num', text: 'Преброено' }), el('th', { class: 'num', text: 'Разлика' }))),
        tb)),
    foot });
}

// ---- Ръчно движение ----

async function manualTab(area) {
  const panel = el('div', { class: 'panel' }, regHead('Ръчно движение', []));
  area.appendChild(panel);
  let syncMeasure = () => {};
  const { sel, rows } = await articleOptions('', () => syncMeasure());
  if (!rows.length) {
    panel.appendChild(emptyState('▥', 'Няма артикули, които се водят на склад. Добавете стоки в „Артикули".'));
    return;
  }
  const type = el('select', {}, ...MANUAL.map(([v, l]) => el('option', { value: v, text: l })));
  const date = dateInput(localTodayIso());
  const qty = input({ type: 'number', step: 'any', min: '0' });
  const cost = input({ type: 'number', step: 'any', min: '0' });
  const note = input({ maxlength: '500' });
  const measure = el('span', { class: 'cal-hint', text: '' });
  syncMeasure = () => {
    const r = rows.find((x) => String(x.article_id) === sel.value);
    measure.textContent = r ? 'Количество в ' + r.measure + '; налични: ' + fq(r.qty) + ' ' + r.measure + (r.qty > 0 ? ', средна цена ' + f2(r.avg) : '') : '';
  };
  const costHint = el('div', { class: 'cal-hint' });
  const syncType = () => {
    const t = type.value;
    costHint.textContent = t === 'opening' || t === 'receipt'
      ? 'Единичната цена е задължителна.'
      : t === 'adjustment_in' ? 'Празна цена = средната към датата.' : 'Цената на разхода се смята от склада (средна) — полето не се ползва.';
    cost.disabled = t === 'issue' || t === 'adjustment_out';
  };
  type.addEventListener('change', syncType);
  syncType();
  const save = el('button', { class: 'btn', type: 'button', text: 'Запиши движението', onClick: async () => {
    if (!sel.value) { toast('Изберете артикул — пишете името или кода и изберете от списъка.', true); sel.input.focus(); return; }
    if (!date.dataset.iso) { toast('Изберете дата.', true); return; }
    if (!(Number(qty.value) > 0)) { toast('Количеството трябва да е положително число — посоката идва от вида на движението.', true); qty.focus(); return; }
    const body = { company_id: state().activeCompany, type: type.value, date: date.dataset.iso, article_id: Number(sel.value), qty: Number(qty.value), note: note.value.trim() };
    if (!cost.disabled && cost.value.trim() !== '') body.unit_cost = Number(cost.value);
    try {
      const r = await api('/stock/movements', { method: 'POST', body: JSON.stringify(body) });
      toast('Движението е записано.');
      if (r.warnings && r.warnings.length) toast('Внимание: ' + r.warnings.join(' '), true);
      qty.value = ''; cost.value = ''; note.value = '';
      manualReload(area);
    } catch (e) { toast(e.message, true); }
  } });
  panel.appendChild(row(field('Вид', type), field('Дата', date, { size: 's' })));
  panel.appendChild(row(field('Артикул', sel)));
  panel.appendChild(measure);
  panel.appendChild(row(field('Количество', qty, { size: 's' }), field('Единична цена', cost, { size: 's' })));
  panel.appendChild(costHint);
  panel.appendChild(row(field('Основание (бележка)', note)));
  panel.appendChild(el('p', { class: 'cal-hint', text: 'Записаното движение не се редактира и не се трие — грешка се поправя с обратно движение (корекция).' }));
  panel.appendChild(el('div', {}, save));
  openingDateBox(area);
}

// „Дата на началното салдо" (Макс, 21.10.2026): внесено с днешна дата,
// началното салдо не стига до изписването за минал месец (POS за септември
// е на 30.09 → „налични 0"). Администраторът го мести по-рано с един бутон —
// на сървъра това е сторно + същите салда с новата дата.
async function openingDateBox(area) {
  let info;
  try { info = await api('/stock/opening-info' + cq()); } catch (e) { return; }
  const dates = (info && info.dates) || [];
  if (!dates.length) return;
  const box = el('div', { class: 'panel', style: 'margin-top:16px' },
    regHead('Дата на началното салдо', []),
    el('p', { text: 'Началното салдо е с дата ' + dates.map((d) => isoToBg(d.date) + ' (' + plural(d.count, 'артикул', 'артикула') + ')').join(', ') + '.' }),
    el('p', { class: 'cal-hint', text: 'Изписване (напр. продажбите от касата за месец) с по-ранна дата не вижда тази наличност — показва недостиг и цени 0,00. Тогава преместете началното салдо преди първия месец, който изписвате (напр. последния ден на предходния месец).' }));
  if (isAdmin()) {
    const first = dates[0].date;
    const d = dateInput('');
    const btn = el('button', { class: 'btn sm', type: 'button', text: 'Премести датата', onClick: async () => {
      const iso = d.dataset.iso;
      if (!iso) { toast('Изберете новата дата (дд.мм.гггг).', true); d.focus(); return; }
      if (iso > first) { toast('Началното салдо се мести само към по-ранна дата.', true); return; }
      const n = dates.reduce((s2, x) => s2 + x.count, 0);
      const ok = await confirmDialog('Да преместя ли началното салдо (' + plural(n, 'артикул', 'артикула') + ') на ' + isoToBg(iso) + '? Старите записи се сторнират и се записват същите количества и цени с новата дата. Цените на изписванията след нея се преизчисляват сами.', { okText: 'Премести' });
      if (!ok) return;
      try {
        const r = await api('/stock/opening-date', { method: 'POST', body: JSON.stringify({ company_id: state().activeCompany, date: iso }) });
        toast('Началното салдо е преместено на ' + isoToBg(r.date) + ' (' + plural(r.moved, 'артикул', 'артикула') + ').');
        manualReload(area);
      } catch (e) { toast(e.message, true); }
    } });
    box.appendChild(row(field('Нова дата', d, { size: 's' })));
    box.appendChild(el('div', {}, btn));
  } else {
    box.appendChild(el('p', { class: 'cal-hint', text: 'Датата се сменя от администратора.' }));
  }
  area.appendChild(box);
}

function manualReload(area) {
  clear(area);
  manualTab(area);
}
